// src/hooks/useAntiCheat.js
import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook para detectar comportamientos sospechosos durante tests
 * Detecta eventos individuales y patrones de comportamiento
 */
const useAntiCheat = ({ enabled = true, userId = '', testId = '' }) => {
    const [events, setEvents] = useState([]);
    const lastActivityRef = useRef(Date.now());
    const inactivityTimeRef = useRef(0);
    const inactivityIntervalRef = useRef(null);
    const shiftPressedTime = useRef(null);

    // Agregar evento al array
    const addEvent = useCallback((type) => {
        if (!enabled) return;

        const newEvent = {
            type,
            timestamp: new Date().toISOString()
        };

        setEvents(prev => [...prev, newEvent]);
        lastActivityRef.current = Date.now();
    }, [enabled]);

    // Registrar tiempo de inactividad
    useEffect(() => {
        if (!enabled) return;

        inactivityIntervalRef.current = setInterval(() => {
            const now = Date.now();
            const timeSinceLastActivity = (now - lastActivityRef.current) / 1000; // segundos

            if (timeSinceLastActivity > 5) { // Si lleva más de 5 segundos sin actividad
                inactivityTimeRef.current += 1;
            }
        }, 1000);

        return () => {
            if (inactivityIntervalRef.current) {
                clearInterval(inactivityIntervalRef.current);
            }
        };
    }, [enabled]);

    // Event Listeners
    useEffect(() => {
        if (!enabled) return;

        // COPY
        const handleCopy = (e) => {
            addEvent('copy');
        };

        // PASTE
        const handlePaste = (e) => {
            addEvent('paste');
        };

        // CUT
        const handleCut = (e) => {
            addEvent('cut');
        };

        // RIGHT CLICK (contextmenu)
        const handleContextMenu = (e) => {
            addEvent('rightClick');
        };

        // TAB SWITCH / WINDOW BLUR
        const handleBlur = () => {
            // Si Shift fue presionado hace menos de 500ms, probablemente fue Win+Shift+S
            if (shiftPressedTime.current && (Date.now() - shiftPressedTime.current) < 10500) {
                addEvent('printScreen');
                shiftPressedTime.current = null;
                console.log("print");
            } else {
                addEvent('windowBlur');
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                addEvent('tabSwitch');
            }
        };

        // TECLADO - Combinaciones sospechosas
        const handleKeyDown = (e) => {
            // Detectar cuando se presiona Shift (para Win+Shift+S)
            if (e.key === 'Shift') {
                shiftPressedTime.current = Date.now();
            }

            // F12
            if (e.key === 'F12') {
                e.preventDefault();
                addEvent('f12Attempt');
            }

            // Ctrl+Shift+I / Cmd+Option+I (DevTools)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
                e.preventDefault();
                addEvent('devToolsAttempt');
            }

            // Ctrl+Shift+J / Cmd+Option+J (Console)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'J') {
                e.preventDefault();
                addEvent('devToolsAttempt');
            }

            // Ctrl+Shift+C / Cmd+Option+C (Inspect)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
                e.preventDefault();
                addEvent('devToolsAttempt');
            }

            // Ctrl+U / Cmd+U (Ver código fuente)
            if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
                e.preventDefault();
                addEvent('ctrlUAttempt');
            }

            // Ctrl+P / Cmd+P (Imprimir)
            if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
                e.preventDefault();
                addEvent('ctrlPAttempt');
            }

            // Print Screen (múltiples variantes)
            if (e.key === 'PrintScreen' || e.key === 'Print' || e.keyCode === 44) {
                addEvent('printScreen');
            }

            // Windows + Shift + S (Snipping Tool - Windows 10/11)
            if ((e.metaKey || e.key === 'Meta') && e.shiftKey && e.key === 's') {
                e.preventDefault();
                addEvent('printScreen');
            }

            // Mac: Cmd + Shift + 3 (Captura completa)
            if ((e.metaKey || e.key === 'Meta') && e.shiftKey && e.key === '3') {
                addEvent('printScreen');
            }

            // Mac: Cmd + Shift + 4 (Captura selección)
            if ((e.metaKey || e.key === 'Meta') && e.shiftKey && e.key === '4') {
                addEvent('printScreen');
            }

            // Mac: Cmd + Shift + 5 (Herramienta de captura)
            if ((e.metaKey || e.key === 'Meta') && e.shiftKey && e.key === '5') {
                addEvent('printScreen');
            }
        };

        // Agregar listeners
        document.addEventListener('copy', handleCopy);
        document.addEventListener('paste', handlePaste);
        document.addEventListener('cut', handleCut);
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('blur', handleBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup
        return () => {
            document.removeEventListener('copy', handleCopy);
            document.removeEventListener('paste', handlePaste);
            document.removeEventListener('cut', handleCut);
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('blur', handleBlur);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, addEvent]);

    // Detectar patrones sospechosos
    const detectPatterns = useCallback((eventsList) => {
        const patterns = [];
        const patternWindow = 10; // segundos de ventana para detectar patrones

        for (let i = 0; i < eventsList.length - 1; i++) {
            const current = eventsList[i];
            const next = eventsList[i + 1];

            const currentTime = new Date(current.timestamp).getTime();
            const nextTime = new Date(next.timestamp).getTime();
            const timeDiff = (nextTime - currentTime) / 1000; // segundos

            // Patrón 1: COPY → TAB_SWITCH (< 10 seg)
            if (current.type === 'copy' &&
                (next.type === 'tabSwitch' || next.type === 'windowBlur') &&
                timeDiff <= patternWindow) {
                patterns.push({
                    pattern: 'COPY_THEN_TAB_SWITCH',
                    severity: 'HIGH',
                    timeDiff: timeDiff.toFixed(2),
                    timestamps: [current.timestamp, next.timestamp],
                    description: 'Posible consulta externa después de copiar'
                });
            }

            // Patrón 2: TAB_SWITCH → PASTE (< 15 seg)
            if ((current.type === 'tabSwitch' || current.type === 'windowBlur') &&
                next.type === 'paste' &&
                timeDiff <= 15) {
                patterns.push({
                    pattern: 'TAB_SWITCH_THEN_PASTE',
                    severity: 'HIGH',
                    timeDiff: timeDiff.toFixed(2),
                    timestamps: [current.timestamp, next.timestamp],
                    description: 'Posible pegado de información externa'
                });
            }

            // Patrón 3: PRINT_SCREEN → TAB_SWITCH (< 10 seg)
            if (current.type === 'printScreen' &&
                (next.type === 'tabSwitch' || next.type === 'windowBlur') &&
                timeDiff <= patternWindow) {
                patterns.push({
                    pattern: 'PRINT_THEN_TAB_SWITCH',
                    severity: 'CRITICAL',
                    timeDiff: timeDiff.toFixed(2),
                    timestamps: [current.timestamp, next.timestamp],
                    description: 'Captura de pantalla seguida de cambio de contexto'
                });
            }

            // Patrón 4: RIGHT_CLICK → TAB_SWITCH (< 10 seg)
            if (current.type === 'rightClick' &&
                (next.type === 'tabSwitch' || next.type === 'windowBlur') &&
                timeDiff <= patternWindow) {
                patterns.push({
                    pattern: 'RIGHT_CLICK_THEN_TAB_SWITCH',
                    severity: 'MEDIUM',
                    timeDiff: timeDiff.toFixed(2),
                    timestamps: [current.timestamp, next.timestamp],
                    description: 'Intento de búsqueda externa'
                });
            }

            // Patrón 5: CTRL+U → TAB_SWITCH (< 10 seg)
            if (current.type === 'ctrlUAttempt' &&
                (next.type === 'tabSwitch' || next.type === 'windowBlur') &&
                timeDiff <= patternWindow) {
                patterns.push({
                    pattern: 'CTRL_U_THEN_TAB_SWITCH',
                    severity: 'MEDIUM',
                    timeDiff: timeDiff.toFixed(2),
                    timestamps: [current.timestamp, next.timestamp],
                    description: 'Ver código fuente seguido de cambio de contexto'
                });
            }

            // Patrón 6: CTRL+P → TAB_SWITCH (< 10 seg)
            if (current.type === 'ctrlPAttempt' &&
                (next.type === 'tabSwitch' || next.type === 'windowBlur') &&
                timeDiff <= patternWindow) {
                patterns.push({
                    pattern: 'CTRL_P_THEN_TAB_SWITCH',
                    severity: 'MEDIUM',
                    timeDiff: timeDiff.toFixed(2),
                    timestamps: [current.timestamp, next.timestamp],
                    description: 'Intento de imprimir seguido de cambio de contexto'
                });
            }

            // Patrón 6.5: WINDOW_BLUR → TAB_SWITCH (< 15 seg)
            if (current.type === 'windowBlur' &&
                next.type === 'tabSwitch' &&
                timeDiff <= 15) {
                patterns.push({
                    pattern: 'BLUR_THEN_TAB_SWITCH',
                    severity: 'HIGH',
                    timeDiff: timeDiff.toFixed(2),
                    timestamps: [current.timestamp, next.timestamp],
                    description: 'Salió del navegador y luego cambió de pestaña'
                });
            }

            // Patrón 7: DevTools detectado (cualquier intento es crítico)
            if (current.type === 'devToolsAttempt' || current.type === 'f12Attempt') {
                patterns.push({
                    pattern: 'DEVTOOLS_ATTEMPT',
                    severity: 'CRITICAL',
                    timeDiff: 0,
                    timestamps: [current.timestamp],
                    description: 'Intento de abrir herramientas de desarrollo'
                });
            }
        }

        // Patrón 8: Múltiples TAB_SWITCH en corto periodo (3+ en 30 seg)
        const tabSwitches = eventsList.filter(e => e.type === 'tabSwitch' || e.type === 'windowBlur');
        if (tabSwitches.length >= 3) {
            for (let i = 0; i < tabSwitches.length - 2; i++) {
                const first = new Date(tabSwitches[i].timestamp).getTime();
                const third = new Date(tabSwitches[i + 2].timestamp).getTime();
                const timeDiff = (third - first) / 1000;

                if (timeDiff <= 30) {
                    patterns.push({
                        pattern: 'MULTIPLE_TAB_SWITCHES',
                        severity: 'MEDIUM',
                        timeDiff: timeDiff.toFixed(2),
                        timestamps: [tabSwitches[i].timestamp, tabSwitches[i + 2].timestamp],
                        description: 'Múltiples cambios de pestaña en corto periodo'
                    });
                    break; // Solo reportar una vez este patrón
                }
            }
        }

        return patterns;
    }, []);

    // Obtener resumen de eventos de la página actual
    const getEventsSummary = useCallback(() => {
        const summary = {
            copyAttempts: 0,
            pasteAttempts: 0,
            cutAttempts: 0,
            tabSwitches: 0,
            windowBlurs: 0,
            rightClicks: 0,
            devToolsAttempts: 0,
            f12Attempts: 0,
            ctrlPAttempts: 0,
            ctrlUAttempts: 0,
            printScreenAttempts: 0,
            timeInactive: Math.floor(inactivityTimeRef.current)
        };

        events.forEach(event => {
            switch (event.type) {
                case 'copy':
                    summary.copyAttempts++;
                    break;
                case 'paste':
                    summary.pasteAttempts++;
                    break;
                case 'cut':
                    summary.cutAttempts++;
                    break;
                case 'tabSwitch':
                    summary.tabSwitches++;
                    break;
                case 'windowBlur':
                    summary.windowBlurs++;
                    break;
                case 'rightClick':
                    summary.rightClicks++;
                    break;
                case 'devToolsAttempt':
                    summary.devToolsAttempts++;
                    break;
                case 'f12Attempt':
                    summary.f12Attempts++;
                    break;
                case 'ctrlPAttempt':
                    summary.ctrlPAttempts++;
                    break;
                case 'ctrlUAttempt':
                    summary.ctrlUAttempts++;
                    break;
                case 'printScreen':
                    summary.printScreenAttempts++;
                    break;
                default:
                    break;
            }
        });

        // Detectar patrones
        const suspiciousPatterns = detectPatterns(events);

        return {
            events: events,
            summary: summary,
            suspiciousPatterns: suspiciousPatterns,
            timestamp: new Date().toISOString()
        };
    }, [events, detectPatterns]);

    // Resetear eventos para nueva página
    const resetEvents = useCallback(() => {
        setEvents([]);
        inactivityTimeRef.current = 0;
        lastActivityRef.current = Date.now();
    }, []);

    return {
        events,
        getEventsSummary,
        resetEvents
    };
};

export default useAntiCheat;